'use client';

import { useState } from 'react';
import Image from 'next/image';
import { PHASE_STEPS } from './PhaseStepper';
import { withBase } from '@/lib/base-path'

const REFERRAL_OPTIONS = [
  { value: '', label: 'Selecteer...' },
  { value: 'google', label: 'Google' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'referral', label: 'Verwijzing' },
  { value: 'other', label: 'Anders' },
];

interface PreChatFormProps {
  onSubmit: (data: { name: string; email: string; referralSource?: string }) => void;
  isLoading: boolean;
}

export function PreChatForm({ onSubmit, isLoading }: PreChatFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const validate = (): boolean => {
    const newErrors: { name?: string; email?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Naam is verplicht';
    }

    if (!email.trim()) {
      newErrors.email = 'E-mail is verplicht';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Voer een geldig e-mailadres in';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        name: name.trim(),
        email: email.trim(),
        referralSource: referralSource || undefined,
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-off-white">
      <div className="w-full max-w-md">
        {/* SPAIK branding */}
        <div className="mb-8">
          <Image src={withBase("/logo.svg")} alt="SPAIK" width={96} height={28} className="h-7 w-auto mb-8" priority />
          <p className="text-sm text-off-black/50 mb-2">AI Discovery</p>
          <h1 className="font-display font-thin text-4xl sm:text-5xl leading-[1.05] text-off-black">
            Een gesprek van een kwartier, een rapport op maat
          </h1>
          <p className="text-off-black/70 mt-4">
            Onze AI stelt je vragen over jullie werk. Aan het eind zie je direct een rapport: waar jullie
            werk staat op de werkcurve, met welke taak je begint en een plan voor 90 dagen.
          </p>
        </div>

        {/* How the conversation goes */}
        <ol className="mb-8 space-y-3" aria-label="Zo verloopt het gesprek">
          {PHASE_STEPS.map((step, i) => (
            <li key={step.id} className="flex gap-3">
              <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full border border-off-black/20 text-xs text-off-black/70">
                {i + 1}
              </span>
              <div>
                <p className="text-sm font-medium text-off-black">{step.label}</p>
                <p className="text-sm text-off-black/55">{step.hint}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="mb-8 -mt-4 pl-9 text-xs text-off-black/45">
          Je kunt tussendoor stoppen. Je voortgang blijft bewaard.
        </p>

        {/* Form card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl border border-off-black/10 p-6 space-y-4"
        >
          <p className="text-sm text-off-black/60 mb-2">
            Eerst je naam en e-mail, zodat we je na afloop kunnen bereiken.
          </p>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-off-black mb-1">
              Naam
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bijv. Jan de Vries"
              className={`w-full px-4 py-2.5 bg-off-white rounded-lg border text-off-black placeholder:text-off-black/30 focus:outline-none focus:ring-2 focus:ring-off-black/15 transition-colors ${
                errors.name ? 'border-red-400' : 'border-off-black/10'
              }`}
              disabled={isLoading}
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-off-black mb-1">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jan@bedrijf.nl"
              className={`w-full px-4 py-2.5 bg-off-white rounded-lg border text-off-black placeholder:text-off-black/30 focus:outline-none focus:ring-2 focus:ring-off-black/15 transition-colors ${
                errors.email ? 'border-red-400' : 'border-off-black/10'
              }`}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-sm text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Referral source */}
          <div>
            <label htmlFor="referral" className="block text-sm font-medium text-off-black mb-1">
              Hoe heb je ons gevonden? <span className="text-off-black/40 font-normal">(optioneel)</span>
            </label>
            <select
              id="referral"
              value={referralSource}
              onChange={(e) => setReferralSource(e.target.value)}
              className="w-full px-4 py-2.5 bg-off-white rounded-lg border border-off-black/10 text-off-black focus:outline-none focus:ring-2 focus:ring-off-black/15 transition-colors"
              disabled={isLoading}
            >
              {REFERRAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-orange-cta hover:bg-orange-hover text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Even geduld...' : 'Start het gesprek'}
          </button>

          <p className="text-xs text-off-black/40 text-center">
            Je gegevens worden alleen gebruikt om het gesprek te personaliseren en om contact op te nemen na afloop.
          </p>
        </form>
      </div>
    </div>
  );
}
